import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import Toast from './Toast';
import './WordUploadModal.css';

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_BYTES   = MAX_FILE_SIZE_MB * 1024 * 1024;

// Required columns per word type
const REQUIRED_COLS = {
  sight:    ['word'],
  scramble: ['word', 'scrambled'],
  category: ['word', 'correct_category', 'distractors'],
};

function deduplicateWords(parsedRows, existingWords) {
  const seenInFile  = new Set();
  const afterIntra  = parsedRows.filter((row) => {
    const key = row.word.toUpperCase();
    if (seenInFile.has(key)) return false;
    seenInFile.add(key);
    return true;
  });
  const existingSet = new Set((existingWords || []).map((w) => w.word.toUpperCase()));
  const afterCross  = afterIntra.filter((r) => !existingSet.has(r.word.toUpperCase()));
  return { deduped: afterCross, removedCount: parsedRows.length - afterCross.length };
}

function parseRow(raw, type) {
  // Normalize headers to lowercase, trim
  const row = {};
  for (const [k, v] of Object.entries(raw)) {
    row[k.trim().toLowerCase()] = typeof v === 'string' ? v.trim() : String(v ?? '').trim();
  }

  if (!row.word) return null;
  const word = row.word.toUpperCase();

  if (type === 'sight') {
    return { word };
  }
  if (type === 'scramble') {
    if (!row.scrambled) return null;
    return { word, scrambled: row.scrambled.toUpperCase() };
  }
  if (type === 'category') {
    if (!row.correct_category) return null;
    const raw_dist = row.distractors || '';
    // Split on | first, fallback to comma
    const distractors = (raw_dist.includes('|')
      ? raw_dist.split('|')
      : raw_dist.split(',')
    ).map((d) => d.trim().toUpperCase()).filter(Boolean);
    return { word, correct_category: row.correct_category.toUpperCase(), distractors };
  }
  return null;
}

const TAB_LABELS = { sight: 'Sight Words', scramble: 'Scramble Words', category: 'Category Words' };

const WordUploadModal = ({ activeTab, existingWords = [], onClose, onConfirmed }) => {
  const fileInputRef = useRef(null);

  const [stage, setStage]         = useState('select');   // 'select' | 'review'
  const [parseError, setParseError] = useState('');
  const [reviewRows, setReviewRows] = useState([]);
  const [removedCount, setRemovedCount] = useState(0);
  const [toast, setToast]         = useState(null);
  const [fileName, setFileName]   = useState('');

  // ── File selection ──────────────────────────────────────────
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError('');

    // 5 MB guard
    if (file.size > MAX_FILE_BYTES) {
      setParseError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — maximum allowed is ${MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (rawRows.length === 0) {
          setParseError('The file appears to be empty or has no data rows.');
          return;
        }

        // Validate required columns using first row keys
        const firstRowKeys = Object.keys(rawRows[0]).map((k) => k.trim().toLowerCase());
        const required = REQUIRED_COLS[activeTab] || [];
        const missing = required.filter((col) => !firstRowKeys.includes(col));
        if (missing.length > 0) {
          setParseError(`Missing required column(s): ${missing.map((c) => `"${c}"`).join(', ')}. Please check your file format.`);
          return;
        }

        // Parse rows
        const parsed = rawRows
          .map((r) => parseRow(r, activeTab))
          .filter(Boolean);

        if (parsed.length === 0) {
          setParseError('No valid rows found after parsing. Please check column names and data.');
          return;
        }

        // Deduplicate
        const { deduped, removedCount: rc } = deduplicateWords(parsed, existingWords);
        setReviewRows(deduped);
        setRemovedCount(rc);
        setStage('review');
      } catch (err) {
        setParseError('Could not read the file. Please make sure it is a valid Excel (.xlsx/.xls) or CSV file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Review: remove a single row ─────────────────────────────
  const removeRow = (idx) => {
    setReviewRows((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Confirm ──────────────────────────────────────────────────
  const handleConfirm = () => {
    if (reviewRows.length === 0) {
      setToast({ message: 'No words to add. All rows were removed.', type: 'info' });
      return;
    }
    onConfirmed(reviewRows, removedCount);
  };

  // ── Reset to select stage ────────────────────────────────────
  const handleReset = () => {
    setStage('select');
    setParseError('');
    setReviewRows([]);
    setRemovedCount(0);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="upload-modal-title">
      <div className="modal-card upload-modal">

        {/* Header */}
        <div className="upload-modal-header">
          <i className="fas fa-file-excel" aria-hidden="true"></i>
          <div>
            <h2 id="upload-modal-title">Upload {TAB_LABELS[activeTab]}</h2>
            <p>Import words from an Excel or CSV file</p>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <div className="upload-modal-body">

          {/* ── SELECT STAGE ── */}
          {stage === 'select' && (
            <>
              {/* Format guide */}
              <div className="upload-format-guide">
                <p className="guide-title">
                  <i className="fas fa-info-circle" aria-hidden="true"></i> Expected column names:
                </p>
                {activeTab === 'sight' && (
                  <code>word</code>
                )}
                {activeTab === 'scramble' && (
                  <><code>word</code>, <code>scrambled</code></>
                )}
                {activeTab === 'category' && (
                  <><code>word</code>, <code>correct_category</code>, <code>distractors</code> <span className="guide-note">(pipe-separated, e.g. VEHICLE|PLANT)</span></>
                )}
                <p className="guide-note">All words are automatically converted to UPPERCASE.</p>
              </div>

              {/* File picker */}
              <label className="file-drop-zone" htmlFor="word-file-input">
                <i className="fas fa-cloud-upload-alt upload-icon" aria-hidden="true"></i>
                <span className="drop-title">Click to browse</span>
                <span className="drop-sub">.xlsx, .xls, .csv — max {MAX_FILE_SIZE_MB} MB</span>
                <input
                  id="word-file-input"
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFile}
                  style={{ display: 'none' }}
                  aria-label="Choose file to upload"
                />
              </label>

              {/* Error */}
              {parseError && (
                <div className="upload-error" role="alert">
                  <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                  {parseError}
                </div>
              )}
            </>
          )}

          {/* ── REVIEW STAGE ── */}
          {stage === 'review' && (
            <>
              {/* Summary banner */}
              <div className="review-banner">
                <i className="fas fa-check-circle" aria-hidden="true"></i>
                <div>
                  <strong>{reviewRows.length} word(s) ready to add</strong> from "{fileName}"
                  {removedCount > 0 && (
                    <span className="dedup-note"> — {removedCount} duplicate(s) removed</span>
                  )}
                </div>
                <button className="change-file-btn" onClick={handleReset}>
                  <i className="fas fa-redo" aria-hidden="true"></i> Change file
                </button>
              </div>

              {reviewRows.length === 0 ? (
                <p className="empty-review">All rows were duplicates or removed.</p>
              ) : (
                <div className="review-table-wrapper">
                  <table className="review-table" aria-label="Words to import">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Word</th>
                        {activeTab === 'scramble'  && <th>Scrambled</th>}
                        {activeTab === 'category'  && <th>Category</th>}
                        {activeTab === 'category'  && <th>Distractors</th>}
                        <th>Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="row-num">{idx + 1}</td>
                          <td><strong>{row.word}</strong></td>
                          {activeTab === 'scramble' && <td>{row.scrambled}</td>}
                          {activeTab === 'category' && <td>{row.correct_category}</td>}
                          {activeTab === 'category' && <td className="distractors">{row.distractors?.join(', ')}</td>}
                          <td>
                            <button
                              className="remove-row-btn"
                              onClick={() => removeRow(idx)}
                              aria-label={`Remove ${row.word}`}
                            >
                              <i className="fas fa-trash" aria-hidden="true"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="upload-modal-actions">
          <button className="btn-modal-cancel" onClick={onClose}>Cancel</button>
          {stage === 'review' && (
            <button
              className="btn-modal-confirm"
              onClick={handleConfirm}
              disabled={reviewRows.length === 0}
            >
              <i className="fas fa-check" aria-hidden="true"></i>
              Add {reviewRows.length} Word{reviewRows.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>

        {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      </div>
    </div>
  );
};

export default WordUploadModal;
